import { getClerkInstance } from "@clerk/clerk-expo";
import type { AuthProvider } from "@configs/custom-app-config-schema";
import { useAppStore } from "@hooks/useAppStore";
import { getSupabaseAuthClient } from "libs/supabase-auth";

export const getAuthProvider = (): AuthProvider => {
	const app = useAppStore.getState().app;
	return app?.auth?.provider ?? "clerk";
};

export const isClerkProvider = () => {
	return getAuthProvider() === "clerk";
};

export const useAuthProvider = () => {
	const provider = useAppStore((state) => state.app?.auth?.provider ?? "clerk");
	return {
		provider,
		isClerk: provider === "clerk",
		isSupabase: provider === "supabase",
	};
};

export const getAuthToken = async (): Promise<string | null> => {
	if (isClerkProvider()) {
		const token = await getClerkInstance().session?.getToken({
			template: "jwt-with-user",
			skipCache: true,
		});
		return token ?? null;
	}

	const supabase = getSupabaseAuthClient();
	const {
		data: { session },
	} = await supabase.auth.getSession();
	return session?.access_token ?? null;
};

export const getLogicalUserId = async (): Promise<string | undefined> => {
	if (isClerkProvider()) {
		const user = await getClerkInstance().user;
		return user?.publicMetadata?.logicalId as string | undefined;
	}

	const supabase = getSupabaseAuthClient();
	const {
		data: { session },
	} = await supabase.auth.getSession();
	return session?.user?.id;
};

export const signOutAuth = async (): Promise<void> => {
	if (isClerkProvider()) {
		await getClerkInstance().signOut();
		return;
	}

	const supabase = getSupabaseAuthClient();
	await supabase.auth.signOut();
};
